import { useCallback, useEffect, useState } from 'react';

import { t } from '../../lib/i18n';
import {
  isNewBidSoundEnabled,
  playNewBidSound,
  setNewBidSoundEnabled,
  type SoundPlayOutcome,
} from '../../lib/sound';
import { toast } from '../../lib/toast';
import { useSessionUser } from '../clients/hooks/use-session-user';
import { triggerCountdownUpdate, triggerVehicleCleanup } from './api';
import { MaintenanceCard } from './maintenance-card';

/** The artboard's explicit three-step destructive flow. */
type CleanupState = 'idle' | 'confirm' | 'done';

interface LogEntry {
  id: number;
  at: Date;
  text: string;
  failed: boolean;
}

/**
 * Settings — `Redesign-D-Admin` / `-Mobile`, screen «Налаштування».
 *
 * This is the one destructive screen in the admin, and the artboard says so in
 * its structure rather than in a warning banner: the cleanup card wears a 3px
 * danger rule, and its button does not delete. It asks. Only the second button,
 * in the confirm block, calls the endpoint — so a single misplaced click cannot
 * remove lots. `cleanup` is that three-state machine, idle → confirm → done.
 *
 * Two numbers the artboard prints are not served by `apps/api`:
 *   · «Під видалення зараз — 214 лотів». `POST /admin/vehicle-cleanup/trigger`
 *     is fire-and-forget and answers `statistics: null` even with `dry_run`, so
 *     there is no count to show. The row keeps its place and says the API does
 *     not count ahead; the confirm sentence is worded without a number rather
 *     than interpolating a guess.
 *   · «Журнал операцій» with server history. There is no log endpoint. The card
 *     shows the runs started in THIS session — real events, honestly scoped —
 *     and says where the rest is missing from.
 */
export function SettingsPage() {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);
  const [cleanup, setCleanup] = useState<CleanupState>('idle');
  const [log, setLog] = useState<LogEntry[]>([]);
  // The sound preference is per operator (BUG-36), keyed by the signed-in
  // admin's id from the shell's existing `GET /users/me` query.
  const { user: operator } = useSessionUser();
  const operatorId = operator?._id;
  const [soundEnabled, setSoundEnabled] = useState<boolean>(() =>
    isNewBidSoundEnabled(operatorId),
  );
  // The id arrives after the first render; re-read once it is known.
  useEffect(() => {
    setSoundEnabled(isNewBidSoundEnabled(operatorId));
  }, [operatorId]);

  const toggleSound = useCallback(() => {
    if (!operatorId) return;
    setSoundEnabled((current) => {
      const next = !current;
      setNewBidSoundEnabled(operatorId, next);
      return next;
    });
  }, [operatorId]);

  // The Test button is the ONE place the chime's outcome is surfaced: the
  // stream hook plays fire-and-forget, so without this an operator cannot
  // tell a muted tab from an asset that was never shipped.
  const handleTestSound = useCallback(() => {
    void playNewBidSound().then((outcome) => {
      if (outcome === 'played') {
        toast.success(t('admin.settings.sound.testPlayed'));
        return;
      }
      toast.error(t(SOUND_FAILURE_KEY[outcome]));
    });
  }, []);

  const record = useCallback((text: string): number => {
    const id = Date.now() + Math.random();
    setLog((entries) => [{ id, at: new Date(), text, failed: false }, ...entries].slice(0, 8));
    return id;
  }, []);

  const markFailed = useCallback((id: number) => {
    setLog((entries) =>
      entries.map((entry) => (entry.id === id ? { ...entry, failed: true } : entry)),
    );
  }, []);

  const handleCountdownRefresh = useCallback(() => {
    if (isRefreshing) return;
    setIsRefreshing(true);
    const id = record(t('admin.settings.log.countdown'));
    toast.success(t('admin.settings.countdownRefreshSuccess'));
    // Fire-and-forget: the server runs it in the background (see
    // AdminMaintenanceController), so the UI never waits on completion.
    triggerCountdownUpdate(2000, true)
      .then(() => setLastRefresh(new Date()))
      .catch((error: unknown) => {
        console.error('Countdown refresh error', error);
        markFailed(id);
        toast.error(t('admin.settings.countdownRefreshFailed'));
      })
      .finally(() => setIsRefreshing(false));
  }, [isRefreshing, markFailed, record]);

  const handleVehicleCleanup = useCallback(() => {
    setCleanup('done');
    const id = record(t('admin.settings.log.cleanup'));
    toast.success(t('admin.settings.vehicleCleanupSuccess'));
    triggerVehicleCleanup().catch((error: unknown) => {
      console.error('Vehicle cleanup error', error);
      markFailed(id);
      toast.error(t('admin.settings.vehicleCleanupFailed'));
    });
  }, [markFailed, record]);

  return (
    <>
      {/* ── Header ──────────────────────────────────────────────────────── */}
      <div>
        <h1 className="text-[26px] leading-[1.1] font-semibold tracking-[-0.02em] lg:text-[30px]">
          {t('admin.settings.headline')}
        </h1>
        <p className="mt-1.5 font-mono text-[10px] tracking-[0.12em] text-cf-ink-subtle uppercase lg:mt-2 lg:text-[11px] lg:tracking-[0.14em]">
          <span className="lg:hidden">{t('admin.settings.eyebrowShort')}</span>
          <span className="hidden lg:inline">{t('admin.settings.eyebrow')}</span>
        </p>
      </div>

      <div className="flex flex-col gap-3 lg:grid lg:grid-cols-[repeat(auto-fit,minmax(380px,1fr))] lg:items-start lg:gap-4">
        {/* ── Countdown refresh ─────────────────────────────────────────── */}
        <MaintenanceCard
          title={t('admin.settings.countdownUpdate.headline')}
          body={t('admin.settings.countdownUpdate.body')}
          bodyShort={t('admin.settings.countdownUpdate.bodyShort')}
          meterLabel={t('admin.settings.countdownUpdate.lastRun')}
          meterLabelShort={t('admin.settings.countdownUpdate.lastRun')}
          meterValue={
            lastRefresh ? clock(lastRefresh) : t('admin.settings.countdownUpdate.neverRun')
          }
        >
          <button
            type="button"
            onClick={handleCountdownRefresh}
            disabled={isRefreshing}
            className="h-11 w-full rounded-cf-md bg-cf-brand px-4 text-sm font-semibold text-white hover:bg-cf-brand-hover disabled:opacity-50 lg:w-fit"
          >
            {isRefreshing
              ? t('admin.settings.countdownUpdate.refreshing')
              : t('admin.settings.countdownUpdate.refresh')}
          </button>
        </MaintenanceCard>

        {/* ── Stale-lot cleanup: idle → confirm → done ──────────────────── */}
        <MaintenanceCard
          destructive
          title={t('admin.settings.vehicleCleanup.headline')}
          body={t('admin.settings.vehicleCleanup.body')}
          bodyShort={t('admin.settings.vehicleCleanup.bodyShort')}
          meterLabel={t('admin.settings.vehicleCleanup.pending')}
          meterLabelShort={t('admin.settings.vehicleCleanup.pendingShort')}
          meterValue={t('admin.settings.vehicleCleanup.pendingUnknown')}
        >
          {cleanup === 'idle' && (
            <button
              type="button"
              onClick={() => setCleanup('confirm')}
              className="h-11 w-full rounded-cf-md border border-cf-danger/35 bg-cf-surface px-4 text-sm font-semibold text-cf-danger hover:bg-cf-danger-tint lg:w-fit"
            >
              {t('admin.settings.vehicleCleanup.run')}
            </button>
          )}

          {cleanup === 'confirm' && (
            <div className="rounded-cf-md bg-cf-danger-tint px-3.5 py-3 text-[13px] leading-[1.5] text-cf-ink-muted">
              <strong className="text-cf-ink">
                {t('admin.settings.vehicleCleanup.confirmLead')}
              </strong>{' '}
              {t('admin.settings.vehicleCleanup.confirmBody')}
              <div className="mt-2.5 flex gap-2">
                <button
                  type="button"
                  onClick={handleVehicleCleanup}
                  className="h-10 flex-1 rounded-cf-md bg-cf-danger px-3.5 text-[13px] font-semibold text-white lg:flex-none"
                >
                  {t('admin.settings.vehicleCleanup.confirmYes')}
                </button>
                <button
                  type="button"
                  onClick={() => setCleanup('idle')}
                  className="h-10 flex-1 rounded-cf-md border border-cf-line bg-cf-surface px-3.5 text-[13px] font-semibold lg:flex-none"
                >
                  {t('admin.settings.vehicleCleanup.confirmNo')}
                </button>
              </div>
            </div>
          )}

          {cleanup === 'done' && (
            <p className="flex items-center gap-2 text-[13px] text-cf-success">
              <span className="h-2 w-2 shrink-0 rounded-cf-xs bg-cf-success" />
              <span className="lg:hidden">{t('admin.settings.vehicleCleanup.doneShort')}</span>
              <span className="hidden lg:inline">{t('admin.settings.vehicleCleanup.done')}</span>
            </p>
          )}
        </MaintenanceCard>

        {/* ── New-bid sound (per-operator preference, read by the stream hook) ── */}
        <MaintenanceCard
          title={t('admin.settings.sound.headline')}
          body={t('admin.settings.sound.body')}
          bodyShort={t('admin.settings.sound.bodyShort')}
          meterLabel={t('admin.settings.sound.state')}
          meterLabelShort={t('admin.settings.sound.stateShort')}
          meterValue={soundEnabled ? t('admin.settings.sound.on') : t('admin.settings.sound.off')}
        >
          <div className="flex gap-2">
            <button
              type="button"
              role="switch"
              aria-checked={soundEnabled}
              onClick={toggleSound}
              disabled={!operatorId}
              className={`h-11 flex-1 rounded-cf-md px-4 text-sm font-semibold disabled:opacity-50 lg:flex-none ${
                soundEnabled
                  ? 'border border-cf-line bg-cf-surface text-cf-ink hover:bg-cf-fill'
                  : 'bg-cf-brand text-white hover:bg-cf-brand-hover'
              }`}
            >
              {soundEnabled ? t('admin.settings.sound.disable') : t('admin.settings.sound.enable')}
            </button>
            <button
              type="button"
              onClick={handleTestSound}
              className="h-11 rounded-cf-md border border-cf-line bg-cf-surface px-4 text-sm font-semibold hover:bg-cf-fill"
            >
              {t('admin.settings.sound.test')}
            </button>
          </div>
        </MaintenanceCard>

        {/* ── Operations log ────────────────────────────────────────────── */}
        <section className="rounded-cf-lg border border-cf-line bg-cf-surface px-4 py-1.5 lg:px-6 lg:py-[22px]">
          {/* The mobile artboard prints this card as bare rows — no heading.
              It only keeps one when the list is empty, because the empty
              sentence has to say WHICH log is missing. */}
          <h2
            className={`text-base font-semibold tracking-[-0.02em] lg:block lg:text-[18px] ${
              log.length === 0 ? 'mt-2.5 block lg:mt-0' : 'hidden'
            }`}
          >
            {t('admin.settings.log.title')}
          </h2>

          {log.length === 0 ? (
            <p className="mt-2 pb-2.5 text-[13px] leading-[1.5] text-cf-ink-subtle lg:pb-0">
              {t('admin.settings.log.empty')}
            </p>
          ) : (
            <div className="lg:mt-1.5">
              {log.map((entry) => (
                <div
                  key={entry.id}
                  className="flex items-baseline gap-2.5 border-b border-cf-line-soft py-2.5 text-[13px] leading-[1.4] lg:gap-3"
                >
                  {/* Desktop puts the clock in its own 96px column, as the
                      artboard does; mobile has no room for it, so it drops
                      under the line it stamps. */}
                  <span className="hidden shrink-0 font-mono text-cf-ink-subtle lg:inline lg:w-24">
                    {clock(entry.at)}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block">{entry.text}</span>
                    <span className="block font-mono text-[11px] text-cf-ink-subtle lg:hidden">
                      {clock(entry.at)}
                    </span>
                  </span>
                  <span
                    className={`shrink-0 font-mono text-[11px] whitespace-nowrap ${
                      entry.failed ? 'text-cf-danger' : 'text-cf-success'
                    }`}
                  >
                    {entry.failed
                      ? t('admin.settings.log.failed')
                      : t('admin.settings.log.started')}
                  </span>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </>
  );
}

/** Toast copy per failed {@link SoundPlayOutcome}; 'played' is the success path. */
const SOUND_FAILURE_KEY: Record<Exclude<SoundPlayOutcome, 'played'>, string> = {
  missing: 'admin.settings.sound.testMissing',
  blocked: 'admin.settings.sound.testBlocked',
  failed: 'admin.settings.sound.testFailed',
  'no-audio': 'admin.settings.sound.testFailed',
};

function clock(date: Date): string {
  return new Intl.DateTimeFormat('uk-UA', { hour: '2-digit', minute: '2-digit' }).format(date);
}
